import { DateRange, DateString } from './date_string.ts';
import { TimeString } from './time_string.ts';

export interface Favorite {
  // TODO(hewitt): Use song slug instead, then turn on favorites for custom songs
  hymnalName: string;
  songNumber: string;
}

export function areFavoritesEqual(lhs: Favorite, rhs: Favorite) {
  return lhs.hymnalName === rhs.hymnalName && lhs.songNumber === rhs.songNumber;
}

export interface SongView {
  songSlug: SongSlug;
  durationMs: number;
  householdToken?: Token;
}

export interface SongViewWithOrg extends SongView {
  orgId?: OrgId;
}

export interface User {
  token: Token;
  name: string;
  email?: string;
  internal?: boolean;
  sysAdmin?: boolean;
  roles?: Roles;
  tutorialsCompleted?: TutorialPages[];
  learn?: LearnProgress; // Learn tab progress (issue #3470); absent until the member first practices
}

export enum TutorialPages {
  FamilyWorship = 'family-worship',
}

// the part of a User stored in the users table's JSONB `data` column (see UsersTable.upsertUser)
export type UserData = Pick<User, 'tutorialsCompleted' | 'learn'>;

// ---- Learn tab (issue #3470) ----------------------------------------------------------------
// Per-user practice progress. Stored in the users table's JSONB `data` column and validated by a
// generated JSON schema, so everything here must stay JSON-serializable (no Date objects). The
// rules that read and update these records live in learn_rules.ts.

export enum LearnLevel {
  Easy = 'easy',
  Medium = 'medium',
  Hard = 'hard',
}

export enum LearnModule {
  PitchMatch = 'pitchMatch',
  RhythmEcho = 'rhythmEcho',
  IntervalTraining = 'intervalTraining', // issue #3721
  SingInSolfege = 'singInSolfege', // issue #3722
  SingRhythm = 'singRhythm', // issue #3723
}

export enum VoicePart {
  Soprano = 'soprano',
  Alto = 'alto',
  Tenor = 'tenor',
  Bass = 'bass',
}

export interface VocalRange {
  lowMidi: number;
  highMidi: number;
}

export interface LearnModuleProgress {
  level: LearnLevel; // level the member plays next (advances when a new level unlocks)
  unlocked: LearnLevel[];
  bestScore: Partial<Record<LearnLevel, number>>; // 0..10 per level, only ever increases
  qualifyingSessions: Partial<Record<LearnLevel, number>>; // sessions at or above the unlock score, per level
  sessions: number; // every session at any level
  // Interval Training's pattern sets (issue #3721): the sessions played on each set, per level, in
  // the level's set order (interval_training.ts). Absent for a module that has none and until a
  // session on a set is recorded; shorter than the level's sets until a later set is played.
  setSessions?: Partial<Record<LearnLevel, number[]>>;
}

export interface LearnPracticeDay {
  date: DateString; // local date on the device that recorded the session
  sessions: number; // sessions completed on that date
}

export interface LearnProgress {
  range?: VocalRange; // absent until the Range Finder has run
  voicePart?: VoicePart; // derived from `range` when it is saved
  modules: Partial<Record<LearnModule, LearnModuleProgress>>;
  streakDays: number; // as of lastPracticeDate; see currentStreak() for the value to show
  lastPracticeDate?: DateString;
  practiceDays: LearnPracticeDay[]; // most recent first, capped (see learn_rules.ts)
  // The member has been through Meet the syllables (issue #3720); absent until then. Per user, so
  // each child in a household gets the screen once.
  metSyllables?: boolean;
  updatedAt: string; // ISO timestamp; lets a client tell a stale local copy from a newer server record
}

// IMPORTANT: Update database table 'permissions' constraint on 'role' when making changes to this enum
export enum Role {
  Administrator = 'administrator',
  Staff = 'staff',
}

export type Roles = Array<{ orgId: OrgId; role: Role }>;

export const staffRoles = [Role.Administrator, Role.Staff];

export type OrgId = number;

export interface HouseholdBase {
  token: Token;
  familyLastName: string;
  users: User[];
  householdEmail?: string;
}

export interface Household extends HouseholdBase {
  householdEmail: string;
  orgIds: OrgId[];
  subscriptionExpirationTimestamp?: number;
  trialStartDate?: DateString;
  lastMeterActivationDate?: DateString;
  status?: HouseholdStatus;
  // Full-library access without a subscription (GitHub issue #3399). Computed by the server on every read:
  // the stored manual flag (set_household_discounted MCP tool, or the one-time Household Choice staff backfill) OR
  // any household member holding a staff role in a Legacy org (neither Household Choice nor Usage-Based, green-lit or
  // not) that the household is currently a member of (GitHub issue #3423). Behaves like a subscription for banners
  // and song gates, but deliberately leaves `status` alone so billing and onboarding logic are unaffected. Read-only
  // for clients - upsertHousehold ignores it.
  isDiscounted?: boolean;
  mostRecentSyncTimestamp?: number;
  surveyResponses?: HouseholdSurveyResponses;
  singingPlanStreak?: HouseholdSingingPlanStreak;
}

export type SongCompletionsThisWeek = { [date: DateString]: SongSlug[] };

export interface HouseholdSingingPlanStreak {
  asOfDate: DateString;
  countOfDays: number;
  // Actually not just this week, should be called songInteractions, avoids hitting massive song_views table.
  songCompletionsThisWeek?: SongCompletionsThisWeek;
}

export interface WeeklySingingPlan {
  days: DailySingingPlan[];
  // TODO(remove after 2025-08-01): no longer produced or read; delete with its generated JSON schema (logs [legacy-route] echo)
  priorStreakInDays?: number;
}

export interface DailySingingPlan {
  date: DateString;
  songs: SingingPlanEntry[];
}

export interface SingingPlanEntry {
  category: string;
  slug: SongSlug;
}

export const hymnOfTheMonthCategory = 'Hymn of the Month';

// song slug formats (note there are no '0' prefixes on song numbers):
//   - hymnal/<hymnal-slug>/song/<song-number> (e.g. "hymnal/cc2020/song/111a")
//   - church/<church-id>/song/<song-number> (e.g. "church/73/song/1")
export type SongSlug = string;

// hymnal slugs are often, but not always, the hymnal initials + publishing year (e.g. 'cc2020')
export type HymnalSlug = string;

export type SongNumber = string;

export interface OrgHymnal {
  hash: string;
  songs: OrgHymnalSong[];
}

export interface OrgHymnalSong {
  slug: SongSlug;
  count: number;
  dates: DateString[];
}

export type ActiveOrgCounts = { [date: DateString]: number };

export interface Subscription {
  householdId?: number;
  paymentProcessor?: PaymentProcessor;
  email?: string;
  customerId?: string;
  subscriptionExpirationTimestamp?: number;
  productId?: string;
}

export enum PaymentProcessor {
  Apple = 'Apple',
  Stripe = 'Stripe',
}

// household Stripe subscription plan level (GitHub issue #3096)
export enum StripeSubscriptionLevel {
  Annual = 'annual',
  Monthly = 'monthly',
}

export enum HouseholdStatus {
  Unsubscribed = 'Unsubscribed',
  Subscribed = 'Subscribed',
}

export type SongTempos = { [songSlug: SongSlug]: number };
export type SongTranspositions = { [songSlug: SongSlug]: number };

// an 11-character YouTube video id, e.g. '4lr0TD1jhxA' (never a URL)
export type YouTubeVideoId = string;
// YouTube practice videos attached to a custom song (GitHub issue #4041): one per part, and/or a whole-choir video
// (one video for all parts, 'all')
export interface PracticeVideos {
  soprano?: YouTubeVideoId;
  alto?: YouTubeVideoId;
  tenor?: YouTubeVideoId;
  bass?: YouTubeVideoId;
  all?: YouTubeVideoId;
}
export type PracticeVideoPart = keyof PracticeVideos;
export type SongPracticeVideos = { [songSlug: SongSlug]: PracticeVideos };

// this base info is returned when getting all orgs - to get a full Org object, request that org
export interface OrgBase extends Partial<OrgTokens> {
  id: OrgId;
  name: string;
  /** the effective displayed location (server-computed): `locationOverride` when set, else derived from `physicalAddress` */
  location: string;
  type: OrgType;
  deleted?: boolean;
}

// Stripe's six-field address shape (postalCode maps to Stripe's postal_code)
// state is optional - much of the world has no state/province - but validation
// (isValidOrgAddress) requires a standard code for US/CA/AU addresses
export interface OrgAddress {
  line1?: string;
  line2?: string;
  /** required free text, e.g. "Visalia" (trimmed on save) */
  city: string;
  /**
   * required for US, CA and AU as the standard state/province code, e.g. "TX", "ON", "NSW";
   * optional free text elsewhere
   */
  state?: string;
  /** postal or ZIP code */
  postalCode?: string;
  /** required ISO 3166-1 alpha-2 country code, e.g. "US", "CA", "GB" for the United Kingdom */
  country: string;
}

// the client may not depend on the existence of these properties immediately after an org change
export interface Org extends OrgBase {
  physicalAddress?: OrgAddress; // required at creation; may be absent on legacy orgs until the Stripe backfill
  billingAddress?: OrgAddress; // present only when it differs from physicalAddress
  /**
   * optional display override for `location`; when absent, the displayed `location` is computed
   * from `physicalAddress` (city + state, country only if outside the US, GB displayed as "UK")
   */
  locationOverride?: string;
  renewalDate?: number;
  /** Legacy household add-on (a "green-lit" org): members opt in and the org pays for their subscriptions */
  areHouseholdsSubscribed?: boolean;
  /**
   * Household Choice org (GitHub issue #3406, formerly "free org"): the church pays nothing and each household
   * decides whether to subscribe (7-day trial, then the paywall on every tab). Set by hand when a Legacy org
   * transitions, and by the Church Sub webhook when a Usage-Based subscription lapses. See TERMINOLOGY.md.
   */
  isHouseholdChoice?: boolean;
  /**
   * Usage-Based org (GitHub issue #3406, formerly "subsidized"): the church pays per active household each month
   * and the whole app is open to every member household. Driven by the Stripe Church Sub subscription webhook,
   * never set by hand. Neither flag set means a Legacy org (annual church subscription; being retired).
   */
  isUsageBased?: boolean;
  // creation timestamp (ms) of the Stripe Church Sub event that last drove the billing booleans;
  // an older event must never regress them (Stripe does not guarantee delivery order)
  churchSubEventTimestamp?: number;
  primaryHymnalId?: number; // TODO(remove after 2025-11-01): once no clients are still using it (logs [legacy-route] echo)
  primaryHymnalIds?: number[];
  hymnals?: Hymnal[];
  emailAddresses?: string[];
  songTempos?: SongTempos;
  songTranspositions?: SongTranspositions; // half-steps (positive or negative)
  churchLiturgySchema?: SongListSchema; // TODO(hewitt): used by schools as well - rename to "songListSchema"
  allowPrinting?: boolean;
  notInterested?: boolean;
  singingPlan?: WeeklySingingPlan;
  hiddenSongs?: SongSlug[];
  // Custom songs' YouTube practice videos (GitHub issue #4041). PUT /api/orgs merges this map per song rather
  // than replacing it (mergeOrgUpsert), so an admin's stale cached copy cannot wipe the other songs' videos: a
  // song the request leaves out keeps its stored videos, and an empty entry ({}) removes that song's videos.
  practiceVideos?: SongPracticeVideos;
  becameOrgId?: OrgId;
}

// church used by client (as opposed to data persisted in the server database)
export interface ClientOrg extends Org {
  mostRecentSyncTimestamp?: number;
  updates?: { id: OrgId } & Partial<Org>;
}

// Note: These values are persisted to the database
export enum OrgType {
  Church = 'Church',
  School = 'School',
  SmallGroup = 'Small Group',
}

// small group is displayed under school
// school & small group are exclusive
// choosing a small group for the church replaces the school
export const churchOrgTypes = [OrgType.Church];
export const churchOrgTypesForChooser = [OrgType.Church, OrgType.SmallGroup];
export const schoolOrgTypes = [OrgType.School, OrgType.SmallGroup];

export type Token = string; // uuid

export interface OrgTokens {
  token: Token;
  adminToken: string;
}

export type HymnalId = number;

export interface Hymnal {
  id: HymnalId;
  name: string;
  // Visible only to internal users - hymnals under development or lacking copyright clearance.
  // Exception: also visible to a church that lists it among its primary hymnals, which is how we
  // keep a non-cleared hymnal generally hidden yet available to select churches (see issue #2933).
  isInternal?: boolean;
  // The church that owns this hymnal is its only consumer (e.g. a church's own hymnal collection).
  isChurchSpecific?: boolean;
  // Shown in the Library, but hidden in the song selector unless the org lists it among its
  // hymnals - introduced for Cantus Christi (2002) (see PR #2232).
  isHiddenInSongSelector?: boolean;
  slug: string;
  publisher?: string;
  songListType?: SongListType;
  // Other titles this same hymnal is published under (issue #3454). Each becomes its own
  // Library entry and hymnals-table row, sharing this hymnal's songs and song slugs.
  alternateTitles?: HymnalAlternateTitle[];
  // Set on a Hymnal that is one of another hymnal's alternate titles: the slug of the hymnal
  // whose songs it shares. Song slugs always use that canonical slug, never the alias's.
  aliasOf?: HymnalSlug;
}

// A second title under which the same book is sold, e.g. "Magnify the Lord" is the Book of
// Common Praise (2017) with a different cover (issue #3454). The alias has no song directory:
// its songs, manifest and song slugs are the canonical hymnal's. It gets its own id so a
// church can select it, and its own name and icon for the Library.
export interface HymnalAlternateTitle {
  id: HymnalId;
  name: string;
  // Identifies the hymnals-table row; never appears in a song slug (alphanumeric, like all slugs)
  slug: HymnalSlug;
  // Icon filename within the canonical hymnal's directory (the canonical's icon.png if omitted)
  icon?: string;
  // Defaults to the canonical hymnal's publisher
  publisher?: string;
}

export interface SongRequest {
  email: string;
  hymnalName: string;
  songNumber: string;
  timestamp: number;
  issue?: Copyright;
}

export interface Issue {
  id?: number;
  hymnalName?: string;
  songNumber?: string;
  timestamp: number;
  description: IssueDescription;
  status?: string;
}

export enum Copyright {
  Music = 'Music', // Music copyright (trumps text copyright)
  Text = 'Text', // Text copyright
}

export enum SongFileExtension {
  Pdf = 'pdf',
  Mid = 'mid',
  Syp = 'syp',
  Xml = 'xml', // .xml files cannot be downloaded (to protect hymnal engravers' assets), but can be uploaded
  XmlSyp = 'xml.syp',
  Mp3 = 'mp3', // Vocals
  M4a = 'm4a', // Vocals
}

export const vocalFileExtensions = [SongFileExtension.Mp3, SongFileExtension.M4a];

export enum SongListType {
  Interval = 'interval',
  Event = 'event',
  WorshipService = 'worship-service',
  Playlist = 'playlist', // generated from a hymnal or single song, persisted to local storage
  Seasonal = 'seasonal', // one song per day, managed by an internal account

  // TODO(hewitt): Deprecated - delete
  WeeklyList = 'weekly-list', // for schools
  HymnOfTheMonth = 'hymn-of-the-month',
}

export type CalendarSongListType = SongListType.Event | SongListType.Seasonal;
export const CalendarSongListTypes = [SongListType.Event, SongListType.Seasonal];

export const defaultPlaylistName = 'Playlist';
export const seasonalSongListName = 'Seasonal';

export enum SongListEntryType {
  Song = 'song',
  Text = 'text',
  Scripture = 'scripture',
  Assignment = 'assignment',
}

export enum IntervalType {
  Week = 'week', // e.g., School Weeks
  Month = 'month',
  Year = 'year',
  Season = 'season',
  Fixed = 'fixed', // e.g., Doxology
  Custom = 'custom', // e.g., School Terms
}

// only certain song lists types support deletion
export type DeletableSongListType = Extract<SongListType, SongListType.Event | SongListType.Interval>;
export const DeletableSongListType = {
  [SongListType.Event]: SongListType.Event,
  [SongListType.Interval]: SongListType.Interval,
  [SongListType.Seasonal]: SongListType.Seasonal,
} as const;
export const isDeletableSongListType = (type: SongListType): type is DeletableSongListType => {
  return type in DeletableSongListType;
};

export type SongListId = number;

export interface SongListBase {
  id?: SongListId; // server assigned (negative temporary client IDs for new lists)
  type: SongListType;
  songs: SongListEntry[];
  name?: string;
}

export interface EventSongList extends SongListBase {
  type: SongListType.Event | SongListType.WorshipService | SongListType.Playlist | SongListType.Seasonal;
  date: DateString;
  time?: TimeString;
}

export interface IntervalSongList extends SongListBase {
  type: SongListType.Interval;
  interval: Interval;
}

export interface SchoolTerm extends IntervalSongList {
  assignmentCompletions?: { [date: DateString]: CompletionPercentage };
}

export type CompletionPercentage = number;

// TODO(hewitt): eliminate in favor of interval song lists
export interface HymnOfTheMonthSongList extends SongListBase {
  type: SongListType.HymnOfTheMonth;
  date: DateString;
  interval: Interval;
}

export type SongList = EventSongList | IntervalSongList | HymnOfTheMonthSongList;

export interface Interval extends DateRange {
  type: IntervalType;
}

export type RowId = number; /* 1-based */

interface SongListEntryBase {
  row: RowId;
}

export interface BoundSongListEntry extends SongListEntryBase {
  slug: SongSlug;
}

export interface UnboundSongListEntry extends SongListEntryBase {
  text: string;
}

export type SongListEntry = BoundSongListEntry | UnboundSongListEntry;

export interface SongListSchemaEntry extends SongListEntryBase {
  type: SongListEntryType;
  label?: string;
  intervalType?: IntervalType;
  assigned?: boolean;
}

export interface SongListSchema {
  rows: SongListSchemaEntry[];
}

export type CopyrightPermissions = { [copyrightHolder: string]: string };

export interface HymnalsManifest {
  [hymnalName: string]: HymnalsManifestEntry;
}

export interface HymnalsManifestEntry extends Hashed {
  id: number;
  publisher: string;
  slug: HymnalSlug;
  // visibility flags copied from HymnalMetadata - see the Hymnal interface for their semantics
  isInternal?: boolean;
  isChurchSpecific?: boolean;
  isHiddenInSongSelector?: boolean;
  alternateTitles?: HymnalAlternateTitle[];
}

export interface Hashed {
  hash: string;
  isManifest?: boolean;
}

export interface HymnalManifest {
  [songNumber: string]: SongManifestEntry;
}

export interface HashedManifest {
  [key: string]: Hashed;
}

export type OrganizationKey = string; // e.g. "224 - Christ the King Church (Stillwater MN)"

export interface CustomMusicManifest {
  [organizationKey: OrganizationKey]: Hashed;
}

export interface Hymn {
  basePath?: string;
  // In-memory only: hymns are built by spreading the manifest entry, so the manifest's content
  // hash rides along. Used to tell whether a song actually changed across a manifest refresh
  // (issue #3517). Never sent to or stored by the server.
  hash?: string;
  slug: SongSlug;
  songNumber: SongNumber;
  hymnal: string; // deprecated - prefer slug
  title: string;
  psalm?: string;
  isPdfNewer?: boolean;
  isXmlEmpty?: boolean;
  messageCounts?: MessageCounts;
  verseCount?: number;
  author?: string;
  composer?: string;
  harmony?: string;
  copyright?: string;
  copyrights?: Copyright[];
  key?: string;
  features?: SongFeature[];
  vocals?: string;
  vocalPartCount?: number;
  beatsPerMeasure?: number[];
  extensions?: SongFileExtension[];
}

export function hymnHasVocals(hymn: Hymn): boolean {
  return !!hymn.vocals || !!hymn.features?.find((feature) => feature === SongFeature.Vocals);
}

// For the song lists that offer playback: an unknown extension list counts as "no MIDI" here, the
// one place that is stricter than isSongFileMissing() below.
export function isMidiFileMissing(hymn: Hymn): boolean {
  return hymn.extensions === undefined || isSongFileMissing(hymn, SongFileExtension.Mid);
}

// Whether the song's manifest entry says it does not have this file, so asking the server for it
// can only 404 (issue #3265). An undefined `extensions` means "unknown", not "missing": old cached
// manifests and the org-hymnal pre-download's synthetic entries may lack the list, and those must
// keep fetching.
export function isSongFileMissing(hymn: Pick<Hymn, 'extensions'>, extension: SongFileExtension): boolean {
  return hymn.extensions !== undefined && !hymn.extensions.includes(extension);
}

export type MessageCounts = {
  [messageType in MessageType]?: number;
};

export enum MessageType {
  Info = 'info',
  Warning = 'warning',
  Error = 'error',
}

export enum SongFeature {
  Vocals = 'Vocals',
}

export interface SongManifestEntry {
  title: string;
  hash: string;
  extensions: SongFileExtension[];
  psalm?: string;
  isPdfNewer?: boolean;
  isXmlEmpty?: boolean;
  messageCounts?: MessageCounts;
  features?: SongFeature[];
  copyrights?: Copyright[];
}

export enum Season {
  Advent = 'Advent',
  Christmas = 'Christmas',
  Epiphany = 'Epiphany',
  // LifeOfChrist = 'Life of Christ',
  Transfiguration = 'Transfiguration',
  AshWednesday = 'Ash Wednesday',
  Lent = 'Lent',
  TriumphalEntry = 'Triumphal Entry',
  Passion = "Christ's Passion",
  Resurrection = "Christ's Resurrection",
  Ascension = "Christ's Ascension",
  Pentecost = 'Pentecost',
  AllSaints = 'All Saints',
  Thanksgiving = 'Thanksgiving',
}

// Shape of each hymnal's metadata.json in the hymnals repo; spread into HymnalsManifestEntry
// by generate_manifests.ts
export interface HymnalMetadata {
  id: number;
  publisher: string;
  slug: string;
  // visibility flags - see the Hymnal interface for their semantics
  isInternal?: boolean;
  isChurchSpecific?: boolean;
  isHiddenInSongSelector?: boolean;
  alternateTitles?: HymnalAlternateTitle[];
  contents?: {
    seasonal: Array<{ season: Season; range: string; betterKnown?: string[] }>;
  };
}

export type CustomMusic = { [orgId: string]: Hymn[] };

export interface AppHashes {
  customMusicHash: string;
  hymnalsHash: string;
  orgHymnalHash?: string;
  // the hash in the served bundle's index-<hash>.js filename, so a client can notice a new build
  // without a service-worker round trip (GitHub issue #3265)
  bundleHash?: string;
}

export const hashLength = 8;

export const AppBundleId = `com.crescendosw.ensemble`;

export const enableAppleServerWebhookLogging = true;

export const iOSAppNewestReleasedVersion = '4.0'; // increment to make clients show an app update button

export enum HouseholdOriginSource {
  Church = 'church',
  School = 'school',
  Friend = 'friend',
  Self = 'self',
}

export enum HouseholdSchedule {
  Morning = 'morning',
  Lunch = 'lunch',
  Dinner = 'dinner',
  Bedtime = 'bedtime',
}

export interface HouseholdSurveyResponses {
  origin?: HouseholdOriginSource;
  schedule?: HouseholdSchedule[];
  optedIntoNotifications?: boolean;
  sharedWithChurch?: boolean;
  sharedWithFamily?: boolean;
  choseOrganization?: boolean; // true = picked a church, false = church wasn't listed, undefined = didn't show the page
}

export enum SocketMessageType {
  UpdateWeeklySongLists = 'update-weekly-song-lists',
  // the server's periodic keep-alive; browsers cannot observe protocol pings, so they watch for
  // this instead to notice a socket that has gone quiet (GitHub issue #3265)
  Heartbeat = 'heartbeat',
  // content-free: the server's music or bundle hashes moved, so run the ordinary update checks now
  // rather than waiting to notice (GitHub issue #3265)
  HashesChanged = 'hashes-changed',
}

export interface SocketMessage {
  type: SocketMessageType;
}

export interface UpdateWeeklySongListMessage extends SocketMessage {
  orgId: OrgId;
}

export interface HashesChangedMessage extends SocketMessage {
  // present when the change is one org's hymnal (a song-list edit): clients of other orgs have
  // nothing to re-check and skip the walk. Absent for the music and bundle hashes, which everyone shares.
  orgIds?: OrgId[];
}

export enum EmailType {
  ThisSundaySongListRequest = 'this-sunday-song-list-request',
  CrashReport = 'crash-report',
  // sales alerts for churches without an administrator (GitHub issue #3400) - sent by the server alone
  AdminlessChurchAlert = 'adminless-church-alert',
  AdminlessChurchDigest = 'adminless-church-digest',
}

// the email types a client (or the MCP send_email tool) may ask the server to send - every other type
// is initiated by the server itself, and the send-email route refuses it
export const clientEmailTypes = [EmailType.ThisSundaySongListRequest, EmailType.CrashReport];

export type ClientEmailInfo = ThisSundaySongListRequestEmail | CrashReportEmail;
export type EmailInfo = ClientEmailInfo | AdminlessChurchAlertEmail | AdminlessChurchDigestEmail;

export interface ThisSundaySongListRequestEmail {
  type: EmailType.ThisSundaySongListRequest;
  date: string;
  churchToken: Token;
}

export interface CrashReportEmail {
  type: EmailType.CrashReport;
  issueId: number;
  bundleHash?: string;
  tag: IssueTag;
  description: string;
}

// how a church came to lose its last administrator (GitHub issue #3400)
export enum AdminRemovalCause {
  PermissionRevoke = 'permission-revoke', // the administrator role was revoked
  UserDelete = 'user-delete', // the household deleted the user
  HouseholdDelete = 'household-delete', // a privileged user deleted the whole household
  MemberHouseholdRemove = 'member-household-remove', // a privileged user removed the household from the org
  PeriodicCheck = 'periodic-check', // noticed by the server's periodic check - the removal path is unknown
}

// what sales needs to know about a church that has no administrator
export interface AdminlessChurch {
  orgId: OrgId;
  name: string;
  location: string;
  memberHouseholdCount: number;
  renewalDate?: number; // milliseconds since the epoch
  adminInviteLink: string;
}

export interface AdminlessChurchContact {
  name: string;
  email?: string;
}

// sent to sales when a church goes from having an administrator to having none
export interface AdminlessChurchAlertEmail {
  type: EmailType.AdminlessChurchAlert;
  church: AdminlessChurch;
  cause: AdminRemovalCause;
  removedAdmins?: AdminlessChurchContact[]; // unknown when found by the periodic check
  removedBy?: AdminlessChurchContact; // unknown when found by the periodic check, or when a household deleted its own member
}

// sent to sales on request: every church that currently has no administrator
export interface AdminlessChurchDigestEmail {
  type: EmailType.AdminlessChurchDigest;
  churches: AdminlessChurch[];
}

export interface EmailReceipt {
  emailInfo: EmailInfo;
  fromEmail: string;
  fromName?: string;
  // whom the email is for. Outside production nothing is sent, and the receipt is still written
  // exactly as production would have sent it (GitHub issue #3412), so email_history is the record of
  // what would have gone out - that is how an email is read on staging or on a per-PR preview
  recipients: MessageRecipient[];
  initiator: Initiator;
  subject: string;
  body: string;
}

/* ripped off from mailchimp */
type MessageRecipientType = 'to' | 'cc' | 'bcc';
export interface MessageRecipient {
  email: string;
  name?: string;
  type?: MessageRecipientType;
}

// feel free to extend this type as necessary
export interface Initiator {
  householdId?: number;
  system?: boolean;
}

export interface RestoreResult {
  orgCount: number;
  songListCount: number;
}

export enum Voice {
  // NOTE: Mixer buttons will be ordered according to this enum

  // more of a musical function than a vocal part
  Descant = 'descant',
  Melody = 'melody', // must specify clef
  Melody1 = 'melody-1', // must specify clef
  Melody2 = 'melody-2', // must specify clef
  Cantor = 'cantor', // common in sea shanties
  Treble = 'treble', // came across it in a church custom song (100-009)

  // vocal parts
  SopranoSolo = 'soprano-solo',
  Soprano1 = 'soprano-1',
  Soprano2 = 'soprano-2',
  Soprano = 'soprano',
  MezzoSoprano = 'mezzo',
  AltoSolo = 'alto-solo',
  Alto1 = 'alto-1',
  Alto2 = 'alto-2',
  Alto = 'alto',
  Contralto = 'contralto',
  TenorSolo = 'tenor-solo',
  Tenor1 = 'tenor-1',
  Tenor2 = 'tenor-2',
  Tenor = 'tenor',
  Baritone = 'baritone',
  BassSolo = 'bass-solo',
  Bass1 = 'bass-1',
  Bass2 = 'bass-2',
  Bass = 'bass',
  CounterTenor = 'counter-tenor',

  // more of a musical function than a vocal part
  Vocal = 'vocal', // undetermined voice (.xml.syp only) - must specify clef
  Group1 = 'group-1', // for multi-part canons
  Group2 = 'group-2', // for multi-part canons
  Group3 = 'group-3', // for multi-part canons
  Group4 = 'group-4', // for multi-part canons

  // instrumental parts (see instrumentVoices) - shown in the mixer like a vocal part
  Flute = 'flute',
  Oboe = 'oboe',
  Violin = 'violin',
  Cello = 'cello',
  Trumpet = 'trumpet',

  // non-vocal parts
  Piano = 'piano', // must specify clef
}

export function sortVoices(voices: Voice[]): Voice[] {
  const voiceLookup = new Map(Object.values(Voice).map((voice, index) => [voice, index]));
  return voices.sort((lhs, rhs) => {
    return (voiceLookup.get(lhs) ?? 0) - (voiceLookup.get(rhs) ?? 0);
  });
}

export const voicesRequiringClef = [Voice.Vocal, Voice.Melody, Voice.Melody1, Voice.Melody2, Voice.Piano];
export const voicesForVocalFiles = [Voice.Soprano, Voice.Alto, Voice.Tenor, Voice.Bass, Voice.Piano];

// instrumental parts that get their own mixer slider but never take part in vocal part splitting
export const instrumentVoices: Voice[] = [Voice.Flute, Voice.Oboe, Voice.Violin, Voice.Cello, Voice.Trumpet];

export function isInstrumentVoice(voice: Voice | undefined): voice is Voice {
  return !!voice && instrumentVoices.includes(voice);
}

export function isSpecifiedVocalVoice(voice: Voice | undefined): voice is Voice {
  return !!voice && voice !== Voice.Vocal && voice !== Voice.Piano && !isInstrumentVoice(voice);
}

export const defaultVoiceNames: Voice[] = [Voice.Soprano, Voice.Alto, Voice.Tenor, Voice.Bass];

export const vocalVoiceNames: Voice[] = [Voice.Soprano, Voice.Alto, Voice.Tenor, Voice.Bass, Voice.Piano];

export type VoiceValues = Partial<Record<Voice, number>>;

export const defaultVoiceValuesNoSoprano: VoiceValues = {
  [Voice.Piano]: 0.25,
};

export const defaultVoiceValues: VoiceValues = {
  ...defaultVoiceValuesNoSoprano,
  [Voice.Soprano]: 1,
};

export function getVoiceValue(voice: Voice, voiceValues: VoiceValues): number {
  return voiceValues[voice] ?? 0.5;
}

// for testing json_schema
export interface TestObject {
  text: string;
  number: number;
  boolean: boolean;
  optional?: string;
}

export interface OrgInfo {
  householdInfo: OrgHouseholdInfo;
  usageInfo: OrgUsageInfo;
  links: { [linkType in OrgLinkType]: string };
}

export enum OrgLinkType {
  AdminInvite = 'admin-invite',
  StaffInvite = 'staff-invite',
  HouseholdInvite = 'household-invite',
  CongregationTutorial = 'congregation-tutorial',
  AdminTutorial = 'admin-tutorial',
  VideoMeeting = 'video-meeting',
}

export interface OrgHouseholdInfo {
  totalHouseholds: number;
  uniqueLastNames: number;
  activeHouseholds: number;
  households: Array<HouseholdBase & { active?: boolean }>;
}

export interface OrgUsageInfo {
  songViews30Days: number;
  songViews60Days: number;
  songViews90Days: number;
  assignmentsCompletedPercent?: number;
  usageHistory: OrgUsageHistoryEntry[];
}

export interface OrgUsageHistoryEntry {
  start: DateString;
  songViewCount: number;
}

export enum IssueTag {
  Crash = 'crash',
  FailurePage = 'failure-page',
  WrongNote = 'wrong-note',
  Tempo = 'tempo',
  Other = 'other',
}

export interface CrashDescription {
  tag: IssueTag.Crash;
  description: string;
  bundleHash?: string;
}

export interface SongIssueDescription {
  tag: IssueTag.WrongNote | IssueTag.Tempo | IssueTag.Other;
  description: string;
  measure: string;
  voice: string;
}

export interface FailurePageDescription {
  tag: IssueTag.FailurePage;
  description: string;
}

export type IssueDescription = CrashDescription | SongIssueDescription | FailurePageDescription;

// ── Morning Huddle ──
// The internal, admin-only daily page: one active and one inactive org, rotated daily so that
// every org in a class is featured before any org resurfaces.

// active = at least one song view in the last 30 days; inactive = none
export type HuddleOrgClass = 'active' | 'inactive';

export interface HuddleTopUser {
  name: string;
  songViewCount: number;
}

/**
 * An org administrator as shown on a huddle card: only what the card renders.
 *
 * Deliberately NOT `User`.  A `User` carries `token`, which is a live bearer credential, and this
 * type is serialized to the browser and returned by the get_morning_huddle MCP tool.  The
 * `token?: never` below is a compile-time guard, not documentation: it makes `User` (whose token is
 * a string) structurally unassignable here, so a future change cannot quietly hand a whole user row
 * to a card and reintroduce the leak.
 */
export interface HuddleAdmin {
  name: string;
  email?: string;
  token?: never;
}

export interface HuddleCard {
  orgClass: HuddleOrgClass;
  orgId: OrgId;
  name: string;
  /** the effective displayed location, e.g. "Visalia CA" */
  location: string;
  type: OrgType;
  householdCount: number;
  songViews30Days: number;
  /** absent when the org has never had a song view */
  daysSinceLastSongView?: number;
  admins: HuddleAdmin[];
  /** the org's ten busiest singers over the last 30 days, most song views first */
  topUsers: HuddleTopUser[];
  /** rule-based conversation starter - populated for the inactive card */
  outreachAngle?: string;
}

export interface Huddle {
  /**
   * The Mountain date this huddle is for - the day the rotation last advanced, which on Saturday and
   * Sunday is the preceding Friday.  Absent only before the rotation has ever advanced.
   */
  date?: DateString;
  /** the next Mountain date the rotation advances: weekdays at 2 AM (see huddle_scheduler.ts) */
  nextHuddleDate: DateString;
  /** one active and one inactive card; empty when a class has no orgs, or before the first advance */
  cards: HuddleCard[];
}

/**
 * A household of the org shown on a huddle card, for the dashboard embedded beneath it.
 *
 * Deliberately NOT `HouseholdBase`.  `HouseholdBase.token` is a live bearer credential, as is the
 * token on each of its `users`, and `OrgInfo` - which the /dashboard page receives - carries both.
 * The huddle must not, so this is a separate projection rather than a reuse, and `token?: never`
 * makes `HouseholdBase` structurally unassignable here (the same guard as [[HuddleAdmin]]).
 */
export interface HuddleHousehold {
  familyLastName: string;
  householdEmail?: string;
  /** schools only: sung recently, or keeping a singing plan streak */
  active?: boolean;
  members: HuddleHouseholdMember[];
  token?: never;
}

/** A member of a HuddleHousehold: only what the embedded dashboard renders, and never a token. */
export interface HuddleHouseholdMember {
  name: string;
  email?: string;
  /** the roles this user holds in the org being shown, so staff can be picked out */
  roles?: Role[];
  token?: never;
}

/**
 * The org dashboard as shown under the huddle's active church card - the same household and usage
 * figures the org's own admins see at /dashboard, minus everything that grants access.
 *
 * `OrgInfo.links` is deliberately absent: the invite links embed the org's adminToken, which is a
 * credential for joining the org as an administrator.
 */
export interface HuddleOrgDashboard {
  householdInfo: {
    totalHouseholds: number;
    uniqueLastNames: number;
    activeHouseholds: number;
    households: HuddleHousehold[];
  };
  usageInfo: OrgUsageInfo;
}

export interface HuddleGitHubIssue {
  number: number;
  title: string;
  url: string;
  labels: string[];
}

/**
 * Summary of an org's consult/demo one-pager from the Google shared drive.
 * Every field is optional on purpose: with no Google credential configured - the normal state - this
 * is an empty object, and the card omits the section entirely rather than showing an error.
 */
export interface HuddleOrgNotes {
  /** 2-3 sentence extractive summary of the notes */
  summary?: string;
  documentName?: string;
  documentUrl?: string;
  /** set only when the notes could not be read; absent when simply unconfigured or not found */
  notice?: string;
}

export interface HuddleGitHubTopTen {
  issues: HuddleGitHubIssue[];
  /**
   * why the list is empty or possibly out of date, e.g. no API token configured, or GitHub was
   * unreachable and this is the last good cache.  An informational absence, never an error.
   */
  notice?: string;
}
